<template>
  <div>
    <div class="sidebar" :class="{ sidebar_opened: sidebarState == 1 }" v-resize="handleResize">
      <div class="sidebar__inner">
        <NuxtLink to="/" class="sidebar__logo-block">
          <div class="sidebar__logo tablet-hidden">
            <div class="sidebar__logo-image"></div>
          </div>
          <strong class="sidebar__title tablet-hidden">{{ langJson[lang].productDatabase }}</strong>
        </NuxtLink>
        <div class="sidebar__box" v-bar="{ preventParentScroll: true }">
          <div v-for="category in categories" class="sidebar-section" :key="category.nodeId">
            <NuxtLink
              :to="setMainCategory(category.productBlockType, `/${category.nodeId}/`)"
              custom
              v-slot="{ href, navigate, isActive, isExactActive }"
            >
              <a
                :href="href"
                @click.prevent="
                  setSidebarStates(0);
                  navigate();
                "
                class="sidebar-section__title"
                :class="{
                  'sidebar-section__title_internal': String(category.publishedTo) === '2',
                  'sidebar-section__title_service': String(category.publishedTo) === '3',
                  'router-link-exact-active': isExactActive,
                  'router-link-active': isActive
                }"
              >
                <LockIcon :published-to="category.publishedTo" />
                {{ getCategoryTitle(category) }}
              </a>
            </NuxtLink>
            <ul class="sidebar-section__list" v-if="category.nodes && category.productBlockType !== 'InfoSection'">
              <li
                v-for="subcategory in category.nodes"
                class="sidebar-section__list-item"
                :class="{
                  'sidebar-section__list-item_internal': String(subcategory.publishedTo) === '2',
                  'sidebar-section__list-item_service': String(subcategory.publishedTo) === '3'
                }"
                :key="subcategory.nodeId"
              >
                <NuxtLink
                  :to="
                    setMainCategory(
                      category.productBlockType,
                      `/${category.nodeId}/${subcategory.nodeId}/${getFirstSubId(category, subcategory)}`
                    )
                  "
                  custom
                  v-slot="{ href, navigate, isActive, isExactActive }"
                >
                  <a
                    :href="href"
                    @click.prevent="
                      setSidebarStates(0);
                      navigate();
                    "
                    :class="{
                      'router-link-exact-active': isExactActive,
                      'router-link-active': isActive
                    }"
                  >
                    <LockIcon :published-to="subcategory.publishedTo" />
                    {{ getCategoryTitle(subcategory) }}
                  </a>
                </NuxtLink>
              </li>
            </ul>
          </div>
          <div class="sidebar-section">
            <login />
          </div>
        </div>
      </div>
    </div>
    <overlay :isOverlayVisible="sidebarState == 1" @onOverlayClicked="setSidebarStates(0)" />
  </div>
</template>

<script setup lang="ts">
  import { useMainStore } from '@/stores/main';
  import { setProperFieldExternalFirst } from '@/utils/fieldGetters';

  const mainStore = useMainStore();
  const sidebarState = computed(() => mainStore.sidebarState);
  const lang = computed(() => mainStore.lang);
  const langJson = computed(() => mainStore.langJson);
  const categories = computed(() => mainStore.categories);

  function setMainCategory(productBlockType: string | undefined, suffix: string = '/'): string {
    const map = mainStore.mainCategories || {};
    if (productBlockType === 'DownloadSection') return `${map.downloads || '/downloads'}${suffix}`;
    if (productBlockType === 'InfoSection') return `${map.info || '/info'}${suffix}`;
    return `${map.products || '/products'}${suffix}`;
  }

  function setSidebarStates(num: number): void {
    mainStore.setSidebarChangeState(true);
    mainStore.setSidebarState(num);
  }

  function getFirstSubId(category: any, subcategory: any): string {
    if (
      categories.value.indexOf(category) < 2 &&
      subcategory.nodes &&
      subcategory.nodes.length > 0 &&
      category.productBlockType !== 'DownloadSection' &&
      category.productBlockType !== 'InfoSection'
    ) {
      return String(subcategory.nodes[0].nodeId) + '/';
    } else {
      return '';
    }
  }

  function getCategoryTitle(category: any): string {
    return setProperFieldExternalFirst(category, 'title') || category.title || '';
  }

  function handleResize(): void {
    const width = window.innerWidth;
    if (width > 1023) {
      if (sidebarState.value === 1) mainStore.setSidebarState(0);
    }
  }
</script>

<style>
  .sidebar__box {
    width: 100%;
    left: 0;
    right: 0;
    box-sizing: border-box;
    scrollbar-width: auto;
    scrollbar-color: var(--sidebar-scrollbar-color, rgba(255, 255, 255, 0.28) transparent);
  }
  .sidebar__box::-webkit-scrollbar {
    width: 9px;
  }
  .sidebar__box::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.28);
    border-radius: 5px;
  }
  .sidebar__box::-webkit-scrollbar-track {
    background: transparent;
  }
</style>
